/**
 * Common Safety Dashboard preset — the widget catalog, filter dimensions and
 * default layout for the Safety Dashboard page (pages/dashboards/
 * SafetyCommonDashboard.js).
 *
 * Separate from presets/safety.js on purpose: that preset is shared by the Home
 * dashboard and Safety Overview, and both have per-user saved layouts keyed to
 * its widget ids. A new page with its own analytical order must not disturb
 * them. Widgets that already exist there ARE reused by id (see `reuse`) rather
 * than redefined, so a fix to one lands on both surfaces.
 *
 * Layout follows the analytical flow the dashboard is for:
 *   KPI → trend → breakdown → drill-down → source record → action
 * i.e. executive KPIs, then trends, then the "why" analyses (causes, locations,
 * recurrence), then distributions, then leading indicators, then the corrective
 * actions and risks those findings should end in, then site comparison.
 *
 * Every metric key below resolves in the backend registry, and every number on
 * the page is computed from tenant data — there are no literals for KPI values,
 * site names, departments, categories or causes anywhere in this file.
 */
import {
  buildSafetyCatalog, SAFETY_PYRAMID_TIERS, SAFETY_KPI_SECTIONS,
  SAFETY_HEADLINE_METRICS, ACCIDENT_SITE_METRICS,
} from './safety';

/** Executive KPI cards — incident outcomes. */
export const INCIDENT_KPIS = [
  'safety.incidents.total',
  'safety.incidents.high_severity',
  'safety.incidents.lti',
  'safety.incidents.first_aid',
  'safety.near_miss.total',
  'safety.incidents.fatality',
  'safety.incidents.critical',
  'safety.incidents.open',
];

/**
 * Executive KPI cards — performance. TRIR/LTIFR are the registry's existing
 * rate metrics: (events x 200,000) / man-hours from site_workforce, scaled to
 * per-million for the Indian/ILO standard by the header's standard toggle. They
 * render "no data" rather than a misleading figure when no man-hours exist for
 * the period — see registry.rate_metric.
 */
export const PERFORMANCE_KPIS = [
  'safety.trir',
  'safety.ltifr',
  'safety.lwc_rate',
  'safety.capa.open',
  'safety.capa.overdue',
  'safety.audit.compliance_pct',
];

/** Leading indicators — activity that precedes outcomes. */
export const LEADING_KPIS = [
  'safety.observations.total',
  'safety.observations.unsafe_acts',
  'safety.observations.unsafe_conditions',
  'safety.near_miss.total',
  'audit.completed',
  'risk.assessments.total',
  'meetings.total',
  'training.completion_rate',
  'safety.capa.closure_rate',
  'safety.hazards.high_risk_open',
];

/**
 * Leading indicators as a BAR CHART — counts only.
 *
 * Deliberately not LEADING_KPIS: that set mixes counts with percentages
 * (training.completion_rate, safety.capa.closure_rate), and one axis cannot
 * carry both — a 92% bar beside a count of 12 makes the percentage look like
 * the biggest number on the chart. The rates stay in the KPI card group above,
 * where each carries its own unit.
 *
 * safety.hazards.high_risk_open is also excluded: it is an open backlog, not
 * proactive activity done in the period, so it does not belong on an activity
 * chart even though it is a count.
 */
export const LEADING_ACTIVITY_METRICS = [
  'safety.observations.total',
  'safety.observations.unsafe_acts',
  'safety.observations.unsafe_conditions',
  'safety.near_miss.total',
  'audit.completed',
  'risk.assessments.total',
  'meetings.total',
  'training.completed',
];

/**
 * CAPA status KPIs. "Escalated" from the spec has no field in the CAPA model
 * (there is an extension, not an escalation), so it is deliberately absent
 * rather than approximated — see the page's data-limitations note.
 */
export const CAPA_KPIS = [
  'safety.capa.open',
  'capa.closed',
  'safety.capa.overdue',
  'safety.capa.due_soon',
  'safety.capa.pending_approval',
  'capa.effectiveness',
];

/** CAPA age profile — ordered oldest-last, so the shape reads in sequence. */
export const CAPA_AGING_METRICS = [
  'capa.aging.0_7',
  'capa.aging.8_30',
  'capa.aging.31_60',
  'capa.aging.61_90',
  'capa.aging.over_90',
];

/** Risk overview KPIs, from the HIRA module's own risk levels. */
export const RISK_KPIS = [
  'risk.hazards.total',
  'risk.hazards.critical',
  'risk.hazards.high',
  'risk.hazards.medium',
  'risk.hazards.low',
  'risk.assessments.total',
  'risk.assessments.review_overdue',
];

/** Site comparison columns: outcome, risk, action backlog, compliance. */
export const SITE_PERFORMANCE_METRICS = [
  'safety.incidents.total',
  'safety.incidents.high_severity',
  'safety.hazards.high_risk_open',
  'safety.capa.open',
  'safety.capa.overdue',
  'safety.trir',
];

/**
 * Filter dimensions offered in the filter bar. Only dimensions the data model
 * actually supports appear here:
 * - Business Unit is absent: records carry site_id, and the metric layer scopes
 *   to a single site, so a business-unit rollup is not yet expressible.
 * - Employee / Contractor is absent: neither `incidents` nor
 *   `incident_injured_persons` records a worker class.
 * Both are listed as known gaps on the page rather than faked here.
 */
export const SAFETY_COMMON_DIMENSIONS = [
  { key: 'department', label: 'Department' },
  { key: 'area', label: 'Area' },
  { key: 'location', label: 'Location' },
  { key: 'incident_category', label: 'Incident Type' },
  { key: 'severity', label: 'Severity' },
  { key: 'status', label: 'Status' },
  { key: 'cause', label: 'Root Cause Category' },
  { key: 'risk_category', label: 'Risk Category' },
  { key: 'priority', label: 'CAPA Priority' },
  { key: 'reporter', label: 'Reported By' },
];

/**
 * Every widget available on the Common Safety Dashboard.
 *
 * This page is the ONLY safety analytics surface: Safety Overview now redirects
 * here, so the catalog carries the WHOLE shared safety catalog rather than a
 * hand-picked few. Anything a user could once add to Overview through Customize
 * is still addable here.
 *
 * Where an id exists in both, THIS preset's definition wins — its incident trend
 * drills per month, its severity split is a donut with values in the legend, and
 * its category bars are labelled. The shared entries fill in the rest.
 */
export function buildSafetyCommonCatalog() {
  const own = [
    // ---- Executive KPIs ------------------------------------------------
    {
      id: 'kpis-incidents', type: 'kpi-group', colSpan: 2, title: 'Incident KPIs',
      props: { metrics: INCIDENT_KPIS, columns: 4, accents: {} },
    },
    {
      id: 'kpis-performance', type: 'kpi-group', colSpan: 2, title: 'Performance KPIs',
      props: { metrics: PERFORMANCE_KPIS, columns: 3 },
    },

    // ---- Trend ---------------------------------------------------------
    // Counts from /v2/series: each point drills to that month's records, which
    // the legacy trends endpoint cannot do.
    {
      id: 'incident-trend', type: 'series', colSpan: 2,
      title: 'Incident Trend (last 12 months)',
      props: {
        title: 'Incident Trend (last 12 months)',
        metrics: ['safety.incidents.total', 'safety.near_miss.total', 'safety.incidents.lti'],
        chartType: 'line', months: 12, height: 320,
      },
    },

    // ---- Analysis: why ------------------------------------------------
    {
      id: 'top-causes', type: 'top-causes', colSpan: 1, title: 'Top Causes of Incidents',
      props: { title: 'Top 10 Causes of Incidents', limit: 10 },
    },
    {
      id: 'high-risk-locations', type: 'high-risk-locations', colSpan: 1,
      title: 'Top High-Risk Locations',
      props: { title: 'Top 10 High-Risk Locations', limit: 10 },
    },
    {
      id: 'repeat-incidents', type: 'repeat-incidents', colSpan: 2, title: 'Repeat Incidents',
      props: { title: 'Repeat Incidents', minCount: 2, limit: 12 },
    },

    // ---- Distributions -------------------------------------------------
    {
      id: 'incidents-by-severity', type: 'breakdown', colSpan: 1,
      title: 'Incident Severity Distribution',
      props: {
        title: 'Incident Severity Distribution',
        metric: 'safety.incidents.total', groupBy: 'severity',
        chartType: 'donut', showValues: true, height: 300,
      },
    },
    {
      id: 'incidents-by-category', type: 'breakdown', colSpan: 1,
      title: 'Incidents by Category',
      props: {
        title: 'Incidents by Category',
        metric: 'safety.incidents.total', groupBy: 'incident_category',
        chartType: 'hbar', showValues: true, height: 300,
      },
    },

    // ---- Leading indicators -------------------------------------------
    {
      id: 'kpis-leading', type: 'kpi-group', colSpan: 2, title: 'Leading Indicators',
      props: { metrics: LEADING_KPIS, columns: 4 },
    },
    {
      id: 'leading-trend', type: 'series', colSpan: 2,
      title: 'Leading Indicator Trend (last 12 months)',
      props: {
        title: 'Leading Indicator Trend (last 12 months)',
        metrics: ['safety.observations.total', 'safety.near_miss.total', 'audit.completed'],
        chartType: 'line', months: 12, height: 300,
      },
    },

    {
      id: 'leading-activity', type: 'metric-bar', colSpan: 1,
      title: 'Leading Indicator Activity',
      props: { title: 'Leading Indicator Activity', metrics: LEADING_ACTIVITY_METRICS },
    },

    // ---- CAPA performance ---------------------------------------------
    {
      id: 'kpis-capa', type: 'kpi-group', colSpan: 2, title: 'CAPA Performance',
      props: { metrics: CAPA_KPIS, columns: 3 },
    },
    {
      id: 'capa-aging', type: 'metric-bar', colSpan: 1, title: 'CAPA Aging',
      // Age buckets are point-in-time by nature (how long the CAPAs open RIGHT
      // NOW have been open), so they do not follow the date filter — the title
      // says so, since a bar chart has no info affordance to say it elsewhere.
      props: { title: 'CAPA Aging (open backlog, all dates)', metrics: CAPA_AGING_METRICS },
    },
    {
      id: 'capa-open-vs-closed', type: 'series', colSpan: 1,
      title: 'CAPA Raised vs Closed',
      props: {
        title: 'CAPA Raised vs Closed (last 12 months)',
        metrics: ['safety.capa.total', 'capa.closed'], chart_type: 'bar',
        chartType: 'bar', months: 12, height: 300,
      },
    },
    {
      id: 'capa-by-dept', type: 'breakdown', colSpan: 1, title: 'Open CAPAs by Department',
      props: {
        title: 'Open CAPAs by Department',
        metric: 'safety.capa.open', groupBy: 'department', chartType: 'hbar', showValues: true,
      },
    },
    {
      id: 'capa-by-site', type: 'breakdown', colSpan: 1, title: 'Open CAPAs by Site',
      props: {
        title: 'Open CAPAs by Site',
        metric: 'safety.capa.open', groupBy: 'site', chartType: 'hbar', showValues: true,
      },
    },

    // ---- Audit, compliance & training ---------------------------------
    // Audit records carry no site_id, so these are ORGANISATION-WIDE and do not
    // narrow with the site filter. The titles say so — a reader must never take
    // a tenant total for a site total.
    {
      id: 'audits-by-type', type: 'breakdown', colSpan: 1,
      title: 'Audits by Type',
      props: {
        title: 'Audits Completed by Type (organisation-wide)',
        metric: 'audit.completed', groupBy: 'audit_type',
        chartType: 'hbar', showValues: true, height: 300,
      },
    },
    {
      id: 'compliance-register', type: 'compliance-register', colSpan: 2,
      title: 'Legal Compliance Update',
      props: { title: 'Legal Compliance Update', limit: 25 },
    },
    {
      id: 'training-by-topic', type: 'training-breakdown', colSpan: 1,
      title: 'Training by Topic',
      props: { title: 'Training by Topic', groupBy: 'topic', limit: 12 },
    },
    {
      id: 'training-by-category', type: 'training-breakdown', colSpan: 1,
      title: 'EHS Training by Category',
      props: {
        title: 'EHS Training by Category', groupBy: 'category', limit: 12,
        allowRegroup: false,
      },
    },

    // ---- Risk overview ------------------------------------------------
    {
      id: 'kpis-risk', type: 'kpi-group', colSpan: 2, title: 'Risk Overview',
      props: { metrics: RISK_KPIS, columns: 4 },
    },
    {
      id: 'risk-matrix', type: 'risk-matrix', colSpan: 1, title: 'Risk Heat Map',
      props: { title: 'Risk Heat Map', defaultBasis: 'inherent' },
    },

    // ---- Site comparison ----------------------------------------------
    {
      id: 'site-performance', type: 'site-performance', colSpan: 2,
      title: 'Site / Plant Performance',
      props: { title: 'Site / Plant Performance', metrics: SITE_PERFORMANCE_METRICS },
    },

  ];

  const ownIds = new Set(own.map((w) => w.id));
  const shared = buildSafetyCatalog().filter((w) => !ownIds.has(w.id));
  return [...own, ...shared];
}

/**
 * Default layout — the analytical order, then the panels inherited from Safety
 * Overview. Everything else in the catalog (the full KPI grid, the headline
 * strip, the site rate-comparison table, and the Home-oriented hero / AI summary
 * / My Work panels) stays one click away under Customize.
 *
 * The full KPI grid is deliberately NOT here: this page already shows those
 * metrics in five purpose-built groups, and rendering both would print the same
 * figure twice on one page.
 */
export function safetyCommonDefaultLayout() {
  const spans = {
    'kpis-incidents': 2,
    'kpis-performance': 2,
    'incident-trend': 2,
    'top-causes': 1,
    'high-risk-locations': 1,
    'repeat-incidents': 2,
    'incidents-by-severity': 1,
    'incidents-by-category': 1,
    'kpis-leading': 2,
    'leading-trend': 2,
    'leading-activity': 1,
    'audits-by-type': 1,
    'compliance-register': 2,
    'training-by-topic': 1,
    'training-by-category': 1,
    'kpis-capa': 2,
    'capa-aging': 1,
    'capa-open-vs-closed': 1,
    'capa-by-dept': 1,
    'capa-by-site': 1,
    'kpis-risk': 2,
    'risk-matrix': 1,
    pyramid: 1,
    'site-performance': 2,
    workforce: 2,
    'high-risk-sites': 1,
    'incident-yoy': 2,
    'incidents-by-site': 1,
    'type-by-dept': 2,
    'type-by-location': 2,
    'accident-by-site': 1,
  };
  return [
    'kpis-incidents',
    'kpis-performance',
    'incident-trend',
    'top-causes',
    'high-risk-locations',
    'repeat-incidents',
    'incidents-by-severity',
    'incidents-by-category',
    'kpis-leading',
    'leading-trend',
    'leading-activity',
    'audits-by-type',
    'compliance-register',
    'training-by-topic',
    'training-by-category',
    'kpis-capa',
    'capa-aging',
    'capa-open-vs-closed',
    'capa-by-dept',
    'capa-by-site',
    'kpis-risk',
    'risk-matrix',
    'pyramid',
    'site-performance',
    // Carried over from Safety Overview, which now redirects here. These were on
    // that page by default, so they stay visible by default — a consolidation
    // that quietly dropped panels would just move the problem.
    'workforce',
    'high-risk-sites',
    'incident-yoy',
    'incidents-by-site',
    'type-by-dept',
    'type-by-location',
    'accident-by-site',
  ].map((id) => ({ id, colSpan: spans[id] || 1 }));
}

/**
 * Export spec for this page. The PDF/Excel exporter understands KPI sections,
 * the incident trend, the Heinrich pyramid, dimension breakdowns and per-site
 * comparisons; the four new analyses (causes, high-risk locations, repeat
 * incidents, heat map) are not among its block types, so they are NOT claimed
 * here — each carries its own PNG/CSV download instead. Better a report that
 * omits a panel than one that silently drops it.
 */
export function safetyCommonExportSpec(visibleIds) {
  const set = new Set(Array.isArray(visibleIds) ? visibleIds : []);
  const sections = [];
  if (set.has('kpis-incidents')) sections.push({ label: 'Incident KPIs', accent: 'indigo', metrics: INCIDENT_KPIS });
  if (set.has('kpis-performance')) sections.push({ label: 'Performance KPIs', accent: 'blue', metrics: PERFORMANCE_KPIS });
  if (set.has('kpis-leading')) sections.push({ label: 'Leading Indicators', accent: 'teal', metrics: LEADING_KPIS });
  if (set.has('kpis-capa')) sections.push({ label: 'CAPA Performance', accent: 'amber', metrics: CAPA_KPIS });
  if (set.has('kpis-risk')) sections.push({ label: 'Risk Overview', accent: 'red', metrics: RISK_KPIS });
  if (set.has('workforce')) {
    sections.push({
      label: 'Workforce', accent: 'sky',
      metrics: ['workforce.employees', 'workforce.contractors', 'workforce.consultants',
        'workforce.manhours', 'workforce.employee_manhours', 'workforce.contractor_manhours'],
    });
  }
  // The full KPI grid and the headline strip are only reachable via Customize;
  // when a reader has added one, the export carries it.
  if (set.has('kpis')) {
    SAFETY_KPI_SECTIONS.forEach((sec) => sections.push({
      label: sec.label, accent: sec.accent, metrics: sec.metrics,
    }));
  }
  if (set.has('kpis-headline')) {
    sections.push({ label: 'Headline KPIs', accent: 'blue', metrics: SAFETY_HEADLINE_METRICS });
  }

  const spec = {};
  if (sections.length) {
    spec.sections = sections;
    spec.metrics = sections.flatMap((s) => s.metrics);
  }
  // The on-screen incident trend is the drillable /v2/series chart, so it exports
  // as that same series — one column per measure — rather than as the legacy
  // trend block, which would report different figures from the ones on screen.
  const series = [];
  if (set.has('incident-trend')) {
    series.push({
      title: 'Incident Trend (last 12 months)', accent: 'indigo', months: 12,
      metrics: ['safety.incidents.total', 'safety.near_miss.total', 'safety.incidents.lti'],
    });
  }
  if (set.has('leading-trend')) {
    series.push({
      title: 'Leading Indicator Trend (last 12 months)', accent: 'teal', months: 12,
      metrics: ['safety.observations.total', 'safety.near_miss.total', 'audit.completed'],
    });
  }
  if (set.has('capa-open-vs-closed')) {
    series.push({
      title: 'CAPA Raised vs Closed (last 12 months)', accent: 'amber', months: 12,
      metrics: ['safety.capa.total', 'capa.closed'],
    });
  }
  if (series.length) spec.series = series;

  // The reused rate-trend widget shows TRIR / LTIFR / LWC / near-miss RATE by
  // month. It must not map to the legacy `trend` block, which reports incident
  // COUNTS — that would export different figures under a title claiming rates.
  if (set.has('rate-trend')) {
    spec.rate_trend = {
      title: 'KPI Rate Trend (last 12 months)', months: 12,
      metrics: ['safety.trir', 'safety.ltifr', 'safety.lwc_rate', 'safety.near_miss_rate'],
    };
  }
  // The exporter reads the tier list to compute each band, so it must travel
  // with the block — a title alone renders an empty triangle.
  if (set.has('pyramid')) {
    spec.pyramid = { title: 'Heinrich Safety Triangle', tiers: SAFETY_PYRAMID_TIERS };
  }

  const breakdowns = [];
  if (set.has('incidents-by-severity')) {
    breakdowns.push({ title: 'Incident Severity Distribution', metric: 'safety.incidents.total', group_by: 'severity', chart_type: 'donut' });
  }
  if (set.has('incidents-by-category')) {
    breakdowns.push({ title: 'Incidents by Category', metric: 'safety.incidents.total', group_by: 'incident_category', chart_type: 'hbar' });
  }
  if (set.has('incidents-by-location')) {
    breakdowns.push({ title: 'Incidents by Location', metric: 'safety.incidents.total', group_by: 'location', chart_type: 'hbar' });
  }
  if (set.has('capa-by-dept')) {
    breakdowns.push({ title: 'Open CAPAs by Department', metric: 'safety.capa.open', group_by: 'department', chart_type: 'hbar' });
  }
  if (set.has('capa-by-site')) {
    breakdowns.push({ title: 'Open CAPAs by Site', metric: 'safety.capa.open', group_by: 'site', chart_type: 'hbar' });
  }
  if (set.has('open-by-dept')) {
    breakdowns.push({ title: 'Open Incidents by Department', metric: 'safety.incidents.open', group_by: 'department', chart_type: 'hbar' });
  }
  if (set.has('incidents-by-status')) {
    breakdowns.push({ title: 'Incidents by Status', metric: 'safety.incidents.total', group_by: 'status' });
  }
  if (set.has('incidents-by-reporter')) {
    breakdowns.push({ title: 'Incidents by Reporter', metric: 'safety.incidents.total', group_by: 'reporter', chart_type: 'hbar' });
  }
  if (set.has('incidents-by-site')) {
    breakdowns.push({ title: 'Incidents by Site', metric: 'safety.incidents.total', group_by: 'site', chart_type: 'hbar' });
  }
  // The two stacked pivots (type x department, type x location) export as the
  // single-dimension breakdown, because the exporter has no stacked block. The
  // grouping dimension survives; the type split does not — said here rather than
  // left for a reader to notice a difference between screen and report.
  if (set.has('type-by-dept')) {
    breakdowns.push({ title: 'Incidents by Department (type split not exported)', metric: 'safety.incidents.total', group_by: 'department', chart_type: 'hbar' });
  }
  if (set.has('type-by-location')) {
    breakdowns.push({ title: 'Incidents by Location (type split not exported)', metric: 'safety.incidents.total', group_by: 'location', chart_type: 'hbar' });
  }
  if (set.has('audits-by-type')) {
    // Organisation-wide: audit_records has no site_id, so this block does not
    // narrow with the site filter. The title carries that, in the report as on
    // screen, so a tenant total is never read as a site total.
    breakdowns.push({
      title: 'Audits Completed by Type (organisation-wide)',
      metric: 'audit.completed', group_by: 'audit_type', chart_type: 'hbar',
    });
  }
  if (breakdowns.length) spec.breakdowns = breakdowns;

  const comparisons = [];
  if (set.has('site-performance')) {
    comparisons.push({ title: 'Site / Plant Performance', metrics: SITE_PERFORMANCE_METRICS });
  }
  if (set.has('site-comparison')) {
    comparisons.push({
      title: 'Site Comparison (frequency rates)',
      metrics: ['safety.trir', 'safety.ltifr', 'safety.lwc_rate', 'safety.near_miss_rate'],
    });
  }
  if (set.has('accident-by-site')) {
    comparisons.push({
      title: 'Accident Summary by Site',
      metrics: ACCIDENT_SITE_METRICS.map((m) => m.key),
    });
  }
  if (set.has('high-risk-sites')) {
    // The widget ranks and filters client-side above a TRIR threshold; the export
    // gives every accessible site's rates so the ranking can be seen in full.
    comparisons.push({
      title: 'Site Risk Ranking (TRIR / LTIFR)',
      metrics: ['safety.trir', 'safety.ltifr'],
    });
  }
  if (comparisons.length) spec.comparisons = comparisons;

  if (set.has('incident-yoy')) {
    spec.yoy = {
      title: 'Incidents by Month — Year on Year',
      metric: 'safety.incidents.total', num_years: 3,
    };
  }

  // The four safety analyses and the CAPA age profile. Each is computed by the
  // same service function the widget calls, so an exported figure cannot differ
  // from the one on screen.
  if (set.has('top-causes')) spec.causes = { title: 'Top 10 Causes of Incidents', limit: 10 };
  if (set.has('high-risk-locations')) {
    spec.high_risk_locations = { title: 'Top 10 High-Risk Locations', limit: 10 };
  }
  if (set.has('repeat-incidents')) {
    // Exports the widget's DEFAULT grouping. The widget lets a reader regroup on
    // screen, and that choice is not currently carried into the export.
    spec.repeat_incidents = {
      title: 'Repeat Incidents', group_keys: ['location', 'incident_category'],
      min_count: 2, limit: 12,
    };
  }
  if (set.has('risk-matrix')) spec.risk_matrix = { title: 'Risk Heat Map', basis: 'inherent' };
  const metricBars = [];
  if (set.has('capa-aging')) {
    metricBars.push({
      title: 'CAPA Aging (open backlog, all dates)', accent: 'amber',
      metrics: CAPA_AGING_METRICS,
    });
  }
  if (set.has('leading-activity')) {
    metricBars.push({
      title: 'Leading Indicator Activity', accent: 'teal',
      metrics: LEADING_ACTIVITY_METRICS,
    });
  }
  if (metricBars.length) spec.metric_bars = metricBars;

  // Legal compliance register — a STATE table, so it is not narrowed by the
  // selected period (the block's own note says so in the report).
  if (set.has('compliance-register')) {
    spec.compliance_register = { title: 'Legal Compliance Update', limit: 25 };
  }
  // Training, one block per grouping shown on screen.
  const trainingBreakdowns = [];
  if (set.has('training-by-topic')) {
    trainingBreakdowns.push({ title: 'Training by Topic', group_by: 'topic', limit: 12 });
  }
  if (set.has('training-by-category')) {
    trainingBreakdowns.push({
      title: 'EHS Training by Category', group_by: 'category', limit: 12,
    });
  }
  if (trainingBreakdowns.length) spec.training_breakdowns = trainingBreakdowns;

  warnUnexportedWidgets(set);
  return spec;
}

/**
 * Every widget id this builder knows how to export. Kept beside the builder so
 * the two cannot drift apart silently.
 */
const EXPORTABLE_IDS = new Set([
  'kpis-incidents', 'kpis-performance', 'kpis-leading', 'kpis-capa', 'kpis-risk',
  'incident-trend', 'leading-trend', 'capa-open-vs-closed', 'rate-trend', 'pyramid',
  'incidents-by-severity', 'incidents-by-category', 'incidents-by-location',
  'capa-by-dept', 'capa-by-site', 'open-by-dept', 'incidents-by-status',
  'incidents-by-reporter', 'site-performance', 'top-causes', 'high-risk-locations',
  'repeat-incidents', 'risk-matrix', 'capa-aging',
  'leading-activity', 'audits-by-type', 'compliance-register',
  'training-by-topic', 'training-by-category',
  // Inherited from Safety Overview.
  'workforce', 'high-risk-sites', 'incident-yoy', 'incidents-by-site',
  'type-by-dept', 'type-by-location', 'accident-by-site', 'site-comparison',
  'kpis', 'kpis-headline',
]);

/**
 * Widgets with no export block BY DESIGN, so the warning below flags genuine
 * omissions rather than crying wolf every render.
 *
 * `hero` composes three other panels and has no data of its own; `ai-summary` is
 * generated prose, not figures; `my-work` is one person's task list, which has no
 * place in a report about a site.
 */
const NON_EXPORTABLE_IDS = new Set(['hero', 'ai-summary', 'my-work', 'pss']);

/**
 * Tell a developer, at build time, about a widget on screen that the export
 * would silently omit.
 *
 * The export used to drop the four safety analyses, the CAPA age profile and the
 * series charts — not by decision but because the spec builder was never
 * extended when those widgets were added. Nothing surfaced it; the report simply
 * came out short. This closes that loop: add a widget, forget the export, and the
 * console says so the first time the dashboard renders.
 */
function warnUnexportedWidgets(visibleSet) {
  if (process.env.NODE_ENV === 'production') return;
  const missed = [...visibleSet].filter(
    (id) => !EXPORTABLE_IDS.has(id) && !NON_EXPORTABLE_IDS.has(id),
  );
  if (missed.length) {
    // eslint-disable-next-line no-console
    console.warn(
      '[safety dashboard] these widgets are on screen but have no export block, '
      + 'so the exported report will not contain them: ' + missed.join(', ')
      + '. Add them to safetyCommonExportSpec() and EXPORTABLE_IDS.',
    );
  }
}
